import { useEffect, useMemo, useState } from 'react'

import { useActionableCommentStore } from '@/features/actionable-comment'
import {
  useCheckThreadSync,
  useRetryThreadSync,
} from '@/features/annotation-management'
import {
  ResponseThread,
  ThreadActionableType,
  ThreadSyncStatus,
} from '@/schema'

type TUseTeamworkSyncStatus = {
  thread: ResponseThread
  enabled?: boolean
}

type TUseTeamworkSyncStatusResult = {
  threadSyncStatus: ThreadSyncStatus
  retryThreadSync: () => void
  teamworkSyncUrl: string | null
  teamworkSyncMsg: string | null
  actionableType: ThreadActionableType | null
}

/**
 * Hook to get the teamwork sync status for a thread
 * @param props - The props for the hook
 * @returns The teamwork sync status for a thread
 */
export const useTeamworkSyncStatus = (
  props: TUseTeamworkSyncStatus
): TUseTeamworkSyncStatusResult => {
  const { thread, enabled = true } = props

  const { threadId, isCheckingSync, setIsCheckingSync } =
    useActionableCommentStore()

  const [threadSyncStatus, setThreadSyncStatus] = useState<ThreadSyncStatus>(
    ThreadSyncStatus.NOT_SYNCED
  )
  const [teamworkSyncUrl, setTeamworkSyncUrl] = useState<string | null>(null)
  const [teamworkSyncMsg, setTeamworkSyncMsg] = useState<string | null>(null)
  const [actionableType, setActionableType] =
    useState<ThreadActionableType | null>(null)

  const {
    data: { data: checkThreadSyncData = [] } = {},
    isFetching: isCheckingSyncFetching,
    isLoading: isCheckingSyncLoading,
    isError: isCheckingSyncError,
    dataUpdatedAt,
  } = useCheckThreadSync(thread.thread_id, {
    query: {
      enabled:
        enabled &&
        ((threadId === thread.thread_id && !!isCheckingSync) ||
          threadSyncStatus === ThreadSyncStatus.SYNCING),
    },
  })

  const { mutate: retryThreadSyncMutate } = useRetryThreadSync({
    request: { threadId: thread.thread_id },
    mutation: {
      onSuccess: ({ data }) => {
        const newStatus = data[0].teamwork_sync_status
        if (newStatus && newStatus !== threadSyncStatus) {
          setThreadSyncStatus(newStatus)
        }
      },
    },
  })

  useEffect(() => {
    if (thread.teamwork_sync_status)
      setThreadSyncStatus(thread.teamwork_sync_status)
    if (thread.teamwork_sync_url) setTeamworkSyncUrl(thread.teamwork_sync_url)
    if (thread.teamwork_sync_msg !== undefined)
      setTeamworkSyncMsg(thread.teamwork_sync_msg)
    if (thread.actionable_type) setActionableType(thread.actionable_type)
  }, [thread])

  useEffect(() => {
    if (isCheckingSync && !!threadId) {
      setIsCheckingSync(false)
    }
  }, [isCheckingSync, setIsCheckingSync, threadId])

  useEffect(() => {
    if (
      isCheckingSyncFetching ||
      isCheckingSyncLoading ||
      isCheckingSyncError
    ) {
      return
    }

    if (checkThreadSyncData.length > 0) {
      const syncData = checkThreadSyncData[0]

      if (
        syncData.teamwork_sync_status &&
        syncData.teamwork_sync_status !== threadSyncStatus
      ) {
        setThreadSyncStatus(syncData.teamwork_sync_status)
      }

      if (
        syncData.actionable_type &&
        syncData.actionable_type !== actionableType
      ) {
        setActionableType(syncData.actionable_type)
      }

      if (
        syncData.teamwork_sync_url &&
        syncData.teamwork_sync_url !== teamworkSyncUrl
      ) {
        setTeamworkSyncUrl(syncData.teamwork_sync_url)
      }

      if (
        syncData.teamwork_sync_msg !== undefined &&
        syncData.teamwork_sync_msg !== teamworkSyncMsg
      ) {
        setTeamworkSyncMsg(syncData.teamwork_sync_msg)
      }
    }
  }, [
    checkThreadSyncData,
    isCheckingSyncError,
    isCheckingSyncFetching,
    isCheckingSyncLoading,
    threadSyncStatus,
    teamworkSyncUrl,
    teamworkSyncMsg,
    actionableType,
    dataUpdatedAt,
  ])

  return useMemo(
    () => ({
      threadSyncStatus,
      retryThreadSync: () =>
        retryThreadSyncMutate({ threadId: thread.thread_id }),
      teamworkSyncUrl,
      teamworkSyncMsg,
      actionableType,
    }),
    [
      retryThreadSyncMutate,
      thread.thread_id,
      threadSyncStatus,
      teamworkSyncUrl,
      teamworkSyncMsg,
      actionableType,
    ]
  )
}
